import { normalizeClientPhone } from "./clientPhone.mjs";

export function createReservationFormApi(http) {
    const root = (restaurantId) => `/api/restaurants/${restaurantId}`;
    const data = (promise) => promise.then((response) => response.data);

    return {
        async context(restaurantId, signal, currentWidget = null) {
            const [restaurant, fields, employees, widget] = await Promise.all([
                data(http.get(root(restaurantId), { params: { include: "closures,payment_notif_settings,custom_tags" }, signal })),
                data(http.get(`${root(restaurantId)}/reservations/input-params/fields`, { params: { module: "booking" }, signal })),
                data(http.get(`${root(restaurantId)}/users`, { params: { role: "user_without_account" }, signal })),
                Number(currentWidget?.restaurant_id) === Number(restaurantId)
                    ? Promise.resolve(currentWidget)
                    : data(http.get(`${root(restaurantId)}/widget`, { signal })),
            ]);
            return { restaurant, fields, employees: employees.data, widget };
        },
        async services(restaurantId, date, signal) {
            let ids;
            try {
                ids = await data(http.get(`${root(restaurantId)}/servicesatdate`, { params: { all: true, sorted: true, date }, signal }));
            } catch (error) {
                if (error.response?.status === 422 && !error.response.data?.errors) return [];
                throw error;
            }
            return Promise.all(
                ids.map((id) =>
                    data(
                        http.get(`${root(restaurantId)}/services/${id}`, {
                            params: { include: "slotsWithPax,isOpen,paxDurations,has_seating_plan", date },
                            signal,
                        })
                    )
                )
            );
        },
        seatingPlan(restaurantId, serviceId, date, signal) {
            return data(
                http.get(`${root(restaurantId)}/services/${serviceId}`, {
                    params: { include: "seatingPlanFilled", date },
                    signal,
                })
            );
        },
        searchClients(restaurantId, search, signal) {
            return data(http.get(`${root(restaurantId)}/clients/search`, { params: { search }, signal }));
        },
        async matchingClients(restaurantId, { email, phoneNumber, phoneCountry }, signal) {
            const lookups = [];
            const emailKey = String(email || "")
                .trim()
                .toLowerCase();
            if (/^[^\s@]+@[^\s@]+$/.test(emailKey)) {
                lookups.push(
                    data(http.get(`${root(restaurantId)}/clients/search/multi`, { params: { email: emailKey }, signal })).then((response) =>
                        (response.data || []).filter(
                            (client) =>
                                String(client.email || "")
                                    .trim()
                                    .toLowerCase() === emailKey
                        )
                    )
                );
            }
            const phone = normalizeClientPhone(phoneNumber, phoneCountry);
            if (phone.valid) {
                lookups.push(
                    data(
                        http.get(`${root(restaurantId)}/clients/search/phone`, {
                            params: { phoneNumber: phone.number, phoneCountry: phone.country },
                            signal,
                        })
                    ).then((response) => response.data || [])
                );
            }
            const responses = await Promise.allSettled(lookups);
            const clients = responses.filter((response) => response.status === "fulfilled").flatMap((response) => response.value);
            return [...new Map(clients.map((client) => [String(client.id), client])).values()];
        },
        create(restaurantId, payload, files) {
            const form = new FormData();
            form.append("payload", JSON.stringify(payload));
            files.forEach((file) => form.append("files[]", file));
            return http.post(`${root(restaurantId)}/reservations/add`, form);
        },
        addToWaitingList(restaurantId, payload) {
            return http.post(`${root(restaurantId)}/service/waiting`, payload);
        },
    };
}
